import type { ReactElement } from 'react'
import { Box } from '@chakra-ui/react'
import React, {
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useLocation, useRouteMatch } from 'react-router-dom'
import { parse } from 'query-string'
import { isRestrictedUser } from '../../../../utils/roles'
import { ACLPermissionEnum } from '../../../constants'
import { MLContext, UserContext } from '../../../providers'
import { PublicLinksInfoContext } from '../../../providers/PublicLinksInfoProvider'
import AccessDeniedView from './AccessDeniedView'
import EmptyFolderView from './EmptyFolderView'
import GridViewNew from './GridView/GridViewNew'
import MLGridLoader from './GridView/MLGridLoader'
import SearchGridView from './GridView/SearchGridView'
import NoFilesOrFoldersView from './NoFilesOrFoldersView'
import NotFoundView from './NotFoundView'
import NoResultsFoundView from './NoResultsFoundView'

const MLView = ({
  scrollEl,
  minHeight,
}: {
  scrollEl: HTMLDivElement | null
  minHeight: string
}): ReactElement => {
  const { url } = useRouteMatch()
  const location = useLocation()
  const isSearchPath = url === '/search'
  const queryParams = parse(location.search)
  const isResetting = isSearchPath && Object.keys(queryParams).length === 0

  const [
    {
      folderList,
      fileList,
      recentFileFolderList,
      selectedFiles,
      selectedFolders,
      selectedMediaCollections,
      mediaCollectionList,
      loadingFiles,
      loadingFolders,
      loadingMediaCollections,
      viewableParent,
      folderPermission,
      folderPath,
      filePage,
      folderPage,
      mediaCollectionPage,
      loadingViewableParent,
      loadingFolderPermission,
      mediaCollectionRoute,
      currentMediaCollection,
    },
    dispatch,
  ] = useContext(MLContext)

  const { isPublicLink } = useContext(PublicLinksInfoContext)
  const { user } = useContext(UserContext)

  const routeKey = location.pathname + location.search
  const prevRouteKeyRef = useRef(routeKey)
  const routeChanged = prevRouteKeyRef.current !== routeKey

  // Update "previous" route AFTER this render so this render sees routeChanged === true
  useLayoutEffect(() => {
    prevRouteKeyRef.current = routeKey
  }, [routeKey])

  // ---------- Debounced loader ----------
  const [showLoaderDebounced, setShowLoaderDebounced] = useState(false)
  const debounceRef = useRef<NodeJS.Timeout | null>(null)

  const anyLoading =
    (loadingFiles && filePage === 0) ||
    (loadingFolders && folderPage === 0) ||
    (loadingMediaCollections && mediaCollectionPage === 0) ||
    loadingViewableParent ||
    loadingFolderPermission

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
      debounceRef.current = null
    }
    if (anyLoading) {
      setShowLoaderDebounced(true)
    } else {
      debounceRef.current = setTimeout(
        () => void setShowLoaderDebounced(false),
        100,
      )
    }
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [anyLoading])

  // ---------- View selection ----------
  const childComponent = useMemo<React.ReactElement>(() => {
    // Initial page loading (first page / permission / parent) per mode
    const isInitialPageLoading = isSearchPath
      ? (loadingFiles && filePage === 0) ||
        (loadingFolders && folderPage === 0) ||
        (loadingMediaCollections && mediaCollectionPage === 0)
      : (loadingFiles && filePage === 0) ||
        (loadingFolders && folderPage === 0) ||
        loadingViewableParent ||
        loadingFolderPermission

    // Single guard: show loader & block all empty states while any of these are true
    const shouldShowLoader =
      routeChanged || isResetting || isInitialPageLoading || showLoaderDebounced

    if (isSearchPath) {
      // Loader for search
      if (shouldShowLoader) {
        return <MLGridLoader scrollEl={scrollEl} isSearchPage />
      }

      // Empty search result (only when settled)
      const noResults =
        folderList.length === 0 &&
        fileList.length === 0 &&
        recentFileFolderList.length === 0 &&
        mediaCollectionList.length === 0

      if (noResults) {
        return <NoResultsFoundView minHeight={minHeight} />
      }

      return (
        <SearchGridView
          scrollEl={scrollEl}
          dispatch={dispatch}
          fileList={fileList}
          folderList={folderList}
          mediaCollectionList={mediaCollectionList}
          selectedFiles={selectedFiles}
          selectedFolders={selectedFolders}
          selectedMediaCollections={selectedMediaCollections}
          loadingFiles={loadingFiles}
          loadingFolders={loadingFolders}
          loadingMediaCollections={loadingMediaCollections}
          filePage={filePage}
          folderPage={folderPage}
          mediaCollectionPage={mediaCollectionPage}
          minHeight={minHeight}
        />
      )
    }

    // Non-search path
    if (shouldShowLoader) {
      return <MLGridLoader scrollEl={scrollEl} isSearchPage={false} />
    }

    // Access checks for non-search page
    if (viewableParent === 'NOT_ALLOWED') {
      return <AccessDeniedView minHeight={minHeight} />
    }

    if (viewableParent === 'NOT_FOUND') {
      return <NotFoundView minHeight={minHeight} />
    }

    // Empty state checks for non-search page - original behavior (no mediaCollectionList)
    if (
      !isPublicLink &&
      isRestrictedUser(user?.roles) &&
      (!(
        folderPermission === ACLPermissionEnum.CONTRIBUTE ||
        folderPermission === ACLPermissionEnum.MANAGE
      ) ||
        folderPath === '/') &&
      folderList.length === 0 &&
      fileList.length === 0 &&
      recentFileFolderList.length === 0 &&
      !loadingFiles &&
      !loadingFolders
    ) {
      return <EmptyFolderView minHeight={minHeight} />
    }

    if (isPublicLink && folderList.length === 0 && fileList.length === 0) {
      return <EmptyFolderView minHeight={minHeight} />
    }

    if (
      folderList.length === 0 &&
      fileList.length === 0 &&
      recentFileFolderList.length === 0 &&
      !loadingFiles &&
      !loadingFolders
    ) {
      return <NoFilesOrFoldersView minHeight={minHeight} />
    }

    // Grid
    return (
      <GridViewNew
        scrollEl={scrollEl}
        dispatch={dispatch}
        fileList={fileList}
        folderList={folderList}
        recentFileFolderList={recentFileFolderList}
        mediaCollectionRoute={mediaCollectionRoute ?? false}
        currentMediaCollection={currentMediaCollection ?? ''}
        selectedFiles={selectedFiles}
        selectedFolders={selectedFolders}
        loadingFiles={loadingFiles}
        loadingFolders={loadingFolders}
        filePage={filePage}
        folderPage={folderPage}
        folderPath={folderPath}
        minHeight={minHeight}
      />
    )
  }, [
    folderList,
    fileList,
    mediaCollectionList,
    loadingFiles,
    loadingFolders,
    loadingMediaCollections,
    viewableParent,
    loadingViewableParent,
    folderPermission,
    loadingFolderPermission,
    filePage,
    folderPage,
    mediaCollectionPage,
    folderPath,
    user,
    isPublicLink,
    selectedFiles,
    selectedFolders,
    selectedMediaCollections,
    mediaCollectionRoute,
    currentMediaCollection,
    recentFileFolderList,
    dispatch,
    isSearchPath,
    location.search,
    isResetting,
    showLoaderDebounced,
    routeChanged,
  ])
  return <Box>{childComponent}</Box>
}

export default MLView
